import { lruMemoize } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/browser';

import type { AssetsAssetSchemaTypedWithId } from '@iconik-io/iconik-api/src/services/assets';
import type {
  FilesFormatSchemaWithId,
  FilesFormatSchemaWithIdNullable,
} from '@iconik-io/iconik-api/src/services/files';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import { getProxyMediaType } from '@iconik-io/shared-lib/src/utils/get_proxy_media_type';
import { isPdfFormat } from '@iconik-io/shared-lib/src/utils/is_pdf_format';
import {
  genericMediaTypes,
  isGenericMediaType,
  MEDIA_TYPE_AUDIO,
  MEDIA_TYPE_COLLECTION,
  MEDIA_TYPE_COLLECTION_ISG,
  MEDIA_TYPE_IMAGE,
  MEDIA_TYPE_VIDEO,
  type MediaType,
  subclippableMediaTypes,
} from '@iconik-io/shared-lib/src/utils/media_type';

import { isCollectionMappedToStorage } from './collections';

export const getFormatMediaType = (
  format: FilesFormatSchemaWithIdNullable | undefined,
): MediaType | undefined => {
  const mimeType: string | undefined =
    format?.metadata?.[0]?.internet_media_type;

  if (mimeType) {
    if (mimeType.includes('video') || mimeType.includes('application/mxf')) {
      return MEDIA_TYPE_VIDEO;
    }
    if (mimeType.includes('audio')) {
      return MEDIA_TYPE_AUDIO;
    }
    if (mimeType.includes('image')) {
      return MEDIA_TYPE_IMAGE;
    }
  }
  const components = format?.components;
  if (components?.some(({ type }) => type === 'VIDEO')) {
    return MEDIA_TYPE_VIDEO;
  }
  if (components?.some(({ type }) => type === 'AUDIO')) {
    return MEDIA_TYPE_AUDIO;
  }
  if (components?.every(({ type }) => type === 'IMAGE')) {
    return MEDIA_TYPE_IMAGE;
  }

  return undefined;
};

const searchItemMediaType: Record<
  NonNullable<SearchItemEntity['type']>,
  MediaType
> = {
  ASSET: 'file',
  NLE_PROJECT: 'nle_project',
  CUSTOM: 'custom',
  PLACEHOLDER: 'placeholder',
  LINK: 'link',
  SEQUENCE: 'sequence',
  SUBCLIP: 'subclip',
  POST: 'file',
};

export const getSearchItemMediaType = lruMemoize(
  (
    // Only a handful of fields are read, so a caller assembling a
    // search-item-shaped object need not claim it is a whole one.
    searchItem: Partial<SearchItemEntity> | undefined,
  ): MediaType | undefined => {
    if (!searchItem) {
      return undefined;
    }
    let mediaType: MediaType | undefined;
    switch (searchItem.object_type) {
      case 'assets': {
        if (searchItem.type === 'ASSET') {
          const itemMediaType = searchItem.media_type ?? undefined;
          if (isGenericMediaType(itemMediaType)) {
            mediaType = itemMediaType;
          } else {
            const originalFormat = searchItem.formats?.find((format) => {
              if (format.name === null) {
                Sentry.captureMessage(
                  `Null returned in formats name in asset with ID: ${searchItem.id}`,
                );
              }
              return format.name?.toUpperCase() === 'ORIGINAL';
            });
            mediaType = getFormatMediaType(originalFormat);
          }
        }
        if (searchItem.type === 'SUBCLIP') {
          return 'subclip';
        }
        if (searchItem.type === 'POST') {
          return 'post';
        }
        if (searchItem.type !== 'LINK') {
          const proxy = searchItem.proxies?.[0];
          if (!mediaType && proxy) {
            mediaType = getProxyMediaType(proxy);
          }
        }
        if (!mediaType) {
          mediaType = searchItemMediaType[searchItem.type ?? 'ASSET'];
        }
        break;
      }
      case 'collections': {
        mediaType = isCollectionMappedToStorage(searchItem)
          ? MEDIA_TYPE_COLLECTION_ISG
          : MEDIA_TYPE_COLLECTION;

        break;
      }
      default:
        break;
    }

    return mediaType;
  },
);

export const isFormatGenericMedia = (
  format: FilesFormatSchemaWithId | undefined,
): boolean => genericMediaTypes.has(getFormatMediaType(format));

export const hasMediaFormat = (format: FilesFormatSchemaWithId) =>
  format.metadata?.length &&
  isFormatGenericMedia(format) &&
  format.components?.length;

export const hasNonMediaFormat = (format: FilesFormatSchemaWithId) =>
  !hasMediaFormat(format) && format.name !== 'SUBTITLES';

export const assetHasFaceRecognitionSupport = (
  proxyMediaType: MediaType | undefined,
  assetType: AssetsAssetSchemaTypedWithId['type'],
  assetOriginalFormats: FilesFormatSchemaWithId[],
): boolean => {
  const isAssetTypeSupported = assetType === 'ASSET';
  const hasNoPdfFormats = !assetOriginalFormats.some((format) =>
    isPdfFormat(format?.metadata),
  );
  const isMediaTypeSupported =
    proxyMediaType === MEDIA_TYPE_VIDEO || proxyMediaType === MEDIA_TYPE_IMAGE;

  return isAssetTypeSupported && hasNoPdfFormats && isMediaTypeSupported;
};

export const assetHasSubclipSupport = (
  proxyMediaType: MediaType | undefined,
  assetType: AssetsAssetSchemaTypedWithId['type'] | undefined,
): boolean =>
  assetType === 'ASSET' && subclippableMediaTypes.has(proxyMediaType);
